import React from 'react';

import FormSection from 'containers/FormSectionContainer';
import FieldSet from 'containers/FieldSetContainer';
import FormField from 'containers/FormField';
import ConditionalSection from 'containers/ConditionalSection';
import TextFieldBox from 'components/TextFieldBox';
import NumberFieldBox from 'components/NumberFieldBox';
import CheckboxGroup from 'components/CheckboxGroup';
import RadioButtonGroup from 'components/RadioButtonGroup';
import PartialDate from 'components/PartialDate';
import FormProvider from 'routes/Documents/containers/FormProvider';

import { has, oneOf, fieldIs, neq } from 'lib/conditions';

import { optionAccessor } from './utils';
import { useNullifyDependentFields } from './hooks';
import { nowPartialDate } from 'neuro-utils';

const formatOpt = optionAccessor;

const Form = ({ dispatch, formValues, ...props }) => {
  useNullifyDependentFields({ dispatch, formValues });

  React.useEffect(() => {
    if (props.editing && Object.keys(formValues).length === 0) {
      dispatch(props.change('date', nowPartialDate()));
    }
  }, []);

  return (
    <React.Fragment>
      <FormSection localeId="lifestyle.sections.exercise">
        <FieldSet localeId="lifestyle.labels.excerciseInterval">
          <FormField
            name="excerciseInterval"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'daily' },
              { value: '4to6TimesAWeek' },
              { value: '2to3TimesAWeek' },
              { value: 'onceAWeek' },
              { value: '2to3TimesAMonth' },
              { value: 'CoupleOfTimesAYearOrLess' },
            ]}
          />
        </FieldSet>
      </FormSection>

      <FormSection localeId="lifestyle.sections.nutrition">
        <FieldSet localeId="lifestyle.labels.mealInterval">
          <FormField
            name="mealInterval"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'TwoMealsADay' },
              { value: 'OneOrTwoMealsADay' },
              { value: 'OneMealADay' },
              { value: 'OneMealAlmostEveryDay' },
              { value: 'OneMealOnceOrTwiceADay' },
            ]}
          />
        </FieldSet>

        <FieldSet localeId="lifestyle.labels.diet">
          <FormField
            name="diet"
            component={CheckboxGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'vege' },
              { value: 'vegan' },
              { value: 'lactoseFree' },
              { value: 'glutenFree' },
              { value: 'diabeticsDiet' },
              { value: 'lowCholesterol' },
              { value: 'weightLoss' },
              { value: 'foodAllergy' },
              { value: 'otherDiet' },
            ]}
          />
        </FieldSet>

        <ConditionalSection condition={has('diet', 'otherDiet')}>
          <FieldSet localeId="lifestyle.labels.specialDiet">
            <FormField name="specialDiet" component={TextFieldBox} hintLocale="lifestyle.labels.specialDietHint" />
          </FieldSet>
        </ConditionalSection>

        <ConditionalSection condition={has('diet', 'vege')}>
          <FieldSet localeId="lifestyle.labels.vegetarianSince">
            <FormField name="vegetarianSince" component={PartialDate} precision={1} />
          </FieldSet>
          <FieldSet localeId="lifestyle.labels.eggsAndMilk">
            <FormField name="eggsAndMilk" component={RadioButtonGroup} preset="yesno" />
          </FieldSet>
        </ConditionalSection>

        <ConditionalSection condition={has('diet', 'foodAllergy')}>
          <FieldSet localeId="lifestyle.labels.allergyDetail">
            <FormField name="allergy" component={TextFieldBox} hintLocale="lifestyle.labels.allergyDetailHint" />
          </FieldSet>
        </ConditionalSection>
      </FormSection>

      <FormSection localeId="lifestyle.sections.smokingAndSnus">
        <FieldSet localeId="lifestyle.labels.smoking">
          <FormField
            name="smoking"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'yes', label: 'dictionary.yes' },
              { value: 'smokedBefore' },
              { value: 'no', label: 'dictionary.no' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={oneOf('smoking', ['yes', 'smokedBefore'])}>
          <FieldSet localeId="lifestyle.labels.smokingStartYear">
            <FormField name="smokingStartYear" component={PartialDate} precision={1} />
          </FieldSet>
          <FieldSet localeId="lifestyle.labels.cigarettesADay">
            <FormField
              name="cigarettesADay"
              component={NumberFieldBox}
              hintLocale="lifestyle.labels.cigarettesADayHint"
              min="0"
            />
          </FieldSet>
        </ConditionalSection>
        <ConditionalSection condition={fieldIs('smoking', 'smokedBefore')}>
          <FieldSet localeId="lifestyle.labels.smokingEndYear">
            <FormField name="smokingEndYear" component={PartialDate} precision={1} />
          </FieldSet>
        </ConditionalSection>

        <FieldSet localeId="lifestyle.labels.snus">
          <FormField
            name="snus"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'yes', label: 'dictionary.yes' },
              { value: 'snusedBefore' },
              { value: 'no', label: 'dictionary.no' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={oneOf('snus', ['yes', 'snusedBefore'])}>
          <FieldSet localeId="lifestyle.labels.snusStartYear">
            <FormField name="snusStartYear" component={PartialDate} precision={1} />
          </FieldSet>
          <FieldSet localeId="lifestyle.labels.snusPortionsADay">
            <FormField
              name="snusPortionsADay"
              component={NumberFieldBox}
              hintLocale="lifestyle.labels.snusPortionsADayHint"
              min="0"
            />
          </FieldSet>
        </ConditionalSection>
        <ConditionalSection condition={fieldIs('snus', 'snusedBefore')}>
          <FieldSet localeId="lifestyle.labels.snusEndYear">
            <FormField name="snusEndYear" component={PartialDate} precision={1} />
          </FieldSet>
        </ConditionalSection>
      </FormSection>

      <FormSection localeId="lifestyle.sections.alcohol">
        <FieldSet localeId="lifestyle.labels.alcoholUse">
          <FormField
            name="alcoholUse"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'never' },
              { value: 'month' },
              { value: '43month' },
              { value: '23week' },
              { value: '4orMoreOften' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={neq('alcoholUse', 'never')}>
          <FieldSet localeId="lifestyle.labels.alcoholMoreThan6Interval">
            <FormField
              name="alcoholMoreThan6Interval"
              component={RadioButtonGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'never' },
                { value: 'yearly' },
                { value: 'monthly' },
                { value: 'weekly' },
                { value: 'daily' },
              ]}
            />
          </FieldSet>
        </ConditionalSection>
      </FormSection>

      <FormSection localeId="lifestyle.sections.otherSubstances">
        <FieldSet localeId="lifestyle.labels.usingOtherSubstances">
          <FormField name="usingOtherSubstances" component={RadioButtonGroup} preset="yesno" />
        </FieldSet>
        <ConditionalSection condition={fieldIs('usingOtherSubstances', 'yes')}>
          <FieldSet localeId="lifestyle.labels.otherSubstanceMarijuanaUsageInterval">
            <FormField
              name="otherSubstanceMarijuanaUsageInterval"
              component={RadioButtonGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'never' },
                { value: 'yearly' },
                { value: 'monthly' },
                { value: 'weekly' },
                { value: 'daily' },
              ]}
            />
          </FieldSet>
          <FieldSet localeId="lifestyle.labels.otherSubstancesUsingOther">
            <FormField name="otherSubstancesUsingOther" component={RadioButtonGroup} preset="yesno" />
          </FieldSet>
          <ConditionalSection condition={fieldIs('otherSubstancesUsingOther', 'yes')}>
            <FieldSet localeId="lifestyle.labels.otherSubstanceOtherName">
              <FormField
                name="otherSubstanceOtherName"
                component={TextFieldBox}
                hintLocale="lifestyle.labels.otherSubstanceOtherUsageIntervalHint"
              />
            </FieldSet>
            <FieldSet localeId="lifestyle.labels.otherSubstanceOtherUsageInterval">
              <FormField
                name="otherSubstanceOtherUsageInterval"
                component={RadioButtonGroup}
                optionFormatter={formatOpt}
                options={[
                  { value: 'never' },
                  { value: 'yearly' },
                  { value: 'monthly' },
                  { value: 'weekly' },
                  { value: 'daily' },
                ]}
              />
            </FieldSet>
          </ConditionalSection>
        </ConditionalSection>
      </FormSection>
    </React.Fragment>
  );
};

const LifestyleForm = props => (
  <FormProvider {...props}>
    <Form {...props} />
  </FormProvider>
);

export default LifestyleForm;
